<script>
import EcButton from '../ui/EcButton.vue'
import { useMailV2Store } from '../../stores/mailV2'
import { registerWidgets } from './mailWidgets'

function getTranslatable(val, locale) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[locale] || val.fr || val.en || Object.values(val).find(function (v) { return v }) || ''
}

export default {
  name: 'MailV2Editor',
  components: { EcButton },
  props: {
    templateData: { type: Object, required: true },
    mailType: { type: Object, required: true },
    holderType: { type: String, required: true },
    holderId: { type: [String, Number], required: true },
    locale: { type: String, default: '' },
  },
  data: function () {
    return {
      store: useMailV2Store(),
      activeLocale: this.locale || 'fr',
      saving: false,
      gjsLoaded: false,
      localSubject: {},
      localBodyHtml: {},
      localBodyJson: {},
      localMetadata: {
        from_name: '',
        from_email: '',
        reply_to_email: '',
      },
      sendTestEmail: '',
      sendTestLocale: 'fr',
      showCopyMenu: false,
      showSender: false,
      showTestPanel: false,
      showTypeSettings: false,
      lastFocusedInput: 'editor',
      autocomplete: {
        visible: false,
        x: 0,
        y: 0,
        filter: '',
        items: [],
        selectedIndex: 0,
        rteEl: null,
        savedRange: null,
      },
    }
  },
  computed: {
    locales: function () {
      return (this.mailType && this.mailType.available_locales) || ['fr', 'en']
    },
    variables: function () {
      return (this.mailType && this.mailType.variables) || []
    },
    variablesByGroup: function () {
      var groups = {}
      for (var i = 0; i < this.variables.length; i++) {
        var v = this.variables[i]
        var g = v.group || 'general'
        if (!groups[g]) groups[g] = []
        groups[g].push(v)
      }
      return groups
    },
    filteredAutocompleteItems: function () {
      if (!this.autocomplete.filter) return this.autocomplete.items
      var f = this.autocomplete.filter
      var locale = this.activeLocale
      return this.autocomplete.items.filter(function (v) {
        return v.key.toLowerCase().indexOf(f) !== -1 ||
          (getTranslatable(v.label, locale) || '').toLowerCase().indexOf(f) !== -1
      })
    },
    copyableLocales: function () {
      var self = this
      return this.locales.filter(function (l) { return l !== self.activeLocale })
    },
  },
  watch: {
    templateData: {
      deep: true,
      handler: function () {
        this.initFromTemplate()
      },
    },
  },
  mounted: function () {
    var self = this
    this.loadOrgSettings().then(function () {
      self.initFromTemplate()
      self.initGrapesJS()
    })
    document.addEventListener('click', this.onClickOutsideCopy)
  },
  beforeDestroy: function () {
    this.hideAutocomplete()
    document.removeEventListener('click', this.onClickOutsideCopy)
    if (this._grapesjsEditor) {
      this._grapesjsEditor.destroy()
      this._grapesjsEditor = null
    }
  },
  methods: {
    getTranslatable: getTranslatable,

    varTag: function (key) {
      return '{{ ' + key + ' }}'
    },

    // ── Init ──────────────────────────────────────────────────────────

    initFromTemplate: function () {
      var tmpl = this.templateData
      var subject = {}
      var bodyHtml = {}
      var entries

      entries = Object.entries(tmpl.subject || {})
      for (var i = 0; i < entries.length; i++) {
        if (entries[i][1] != null) subject[entries[i][0]] = entries[i][1]
      }
      this.localSubject = subject

      entries = Object.entries(tmpl.body_html || {})
      bodyHtml = {}
      for (var j = 0; j < entries.length; j++) {
        if (entries[j][1] != null) bodyHtml[entries[j][0]] = entries[j][1]
      }
      this.localBodyHtml = bodyHtml

      this.localBodyJson = Object.assign({}, tmpl.body_json || {})

      var orgSettings = this.store.settings
      this.localMetadata = {
        from_name: (tmpl.metadata && tmpl.metadata.from_name) || (orgSettings && orgSettings.from_name) || '',
        from_email: (tmpl.metadata && tmpl.metadata.from_email) || (orgSettings && orgSettings.from_email) || '',
        reply_to_email: (tmpl.metadata && tmpl.metadata.reply_to_email) || (orgSettings && orgSettings.reply_to_email) || '',
      }
    },

    async loadOrgSettings() {
      try {
        await this.store.getSettings(this.holderType, this.holderId)
      } catch (e) { /* settings not available — ok */ }
    },

    async initGrapesJS() {
      var self = this
      await this.$nextTick()
      var container = this.$refs.editorContainer
      if (!container) return

      try {
        // Import GrapesJS CSS
        var cssId = 'grapesjs-css'
        if (!document.getElementById(cssId)) {
          var link = document.createElement('link')
          link.id = cssId
          link.rel = 'stylesheet'
          link.href = 'https://unpkg.com/grapesjs/dist/css/grapes.min.css'
          document.head.appendChild(link)
          await new Promise(function (resolve) {
            link.onload = function () { resolve() }
            link.onerror = function () { resolve() }
            setTimeout(resolve, 2000)
          })
        }

        var grapesModule = await import('grapesjs')
        var gjs = grapesModule.default

        var newsletterPlugin = null
        try {
          var preset = await import('grapesjs-preset-newsletter')
          newsletterPlugin = preset.default
        } catch (e) {
          // newsletter preset not available
        }

        var plugins = []
        var pluginsOpts = {}

        if (newsletterPlugin) {
          plugins.push(newsletterPlugin)
          pluginsOpts[newsletterPlugin] = {
            modalTitleImport: 'Import HTML',
            modalBtnImport: 'Import',
          }
        }

        self._grapesjsEditor = gjs.init({
          container: container,
          height: '100%',
          width: 'auto',
          fromElement: false,
          storageManager: false,
          plugins: plugins,
          pluginsOpts: pluginsOpts,
          canvas: {
            styles: [
              'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
            ],
          },
          deviceManager: {
            devices: [
              { name: 'Desktop', width: '' },
              { name: 'Tablet', width: '600px', widthMedia: '600px' },
              { name: 'Mobile portrait', width: '320px', widthMedia: '320px' },
            ],
          },
          protectedCss: "\
            body { padding: 32px 40px !important; font-family: 'Poppins', Arial, sans-serif; color: #333; }\
            * { box-sizing: border-box; }\
            table { max-width: 100% !important; }\
            img { max-width: 100% !important; height: auto !important; }\
            @media only screen and (max-width: 480px) {\
              body { padding: 16px !important; }\
              table[width=\"100%\"] td { display: block !important; width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }\
              td[width=\"40%\"], td[width=\"50%\"], td[width=\"60%\"] { display: block !important; width: 100% !important; }\
              h1 { font-size: 22px !important; }\
              h2 { font-size: 18px !important; }\
            }\
          ",
        })

        // Register variable blocks FIRST so they appear at the top
        self.registerVariableBlocks()

        // Register widget blocks (Sections, Contenu, Boutons, etc.)
        registerWidgets(self._grapesjsEditor.BlockManager)

        // Load content for active locale
        self.loadEditorContent()

        // Save content on component update (debounced)
        var saveTimeout = null
        self._grapesjsEditor.on('change:changesCount', function () {
          if (saveTimeout) clearTimeout(saveTimeout)
          saveTimeout = setTimeout(function () { self.saveEditorContent() }, 600)
        })

        // Track focus back to editor when interacting with canvas
        self._grapesjsEditor.on('component:selected', function () {
          self.lastFocusedInput = 'editor'
        })

        // Hook RTE for {{ autocomplete
        self._grapesjsEditor.on('rte:enable', function () {
          var iframeEl = self._grapesjsEditor.Canvas.getFrameEl()
          var iframeDoc = iframeEl && (iframeEl.contentDocument || (iframeEl.contentWindow && iframeEl.contentWindow.document))
          if (iframeDoc) {
            iframeDoc.addEventListener('keyup', self.handleRteKeyup)
            iframeDoc.addEventListener('keydown', self.handleRteKeydown)
            self.$set(self.autocomplete, 'rteEl', iframeDoc)
          }
        })

        self._grapesjsEditor.on('rte:disable', function () {
          self.hideAutocomplete()
          if (self.autocomplete.rteEl) {
            self.autocomplete.rteEl.removeEventListener('keyup', self.handleRteKeyup)
            self.autocomplete.rteEl.removeEventListener('keydown', self.handleRteKeydown)
            self.$set(self.autocomplete, 'rteEl', null)
          }
        })

        self.gjsLoaded = true
      } catch (e) {
        console.warn('[MailV2Editor] GrapesJS not available, using textarea fallback', e)
      }
    },

    registerVariableBlocks: function () {
      if (!this._grapesjsEditor || !this.variables.length) return
      var bm = this._grapesjsEditor.BlockManager

      for (var i = 0; i < this.variables.length; i++) {
        var v = this.variables[i]
        bm.add('var-' + v.key, {
          label: '{{ ' + v.key + ' }}',
          category: 'Variables',
          content: {
            type: 'text',
            content: '{{ ' + v.key + ' }}',
            style: {
              display: 'inline-block',
              padding: '2px 6px',
              background: '#e3f2fd',
              color: '#1565c0',
              'border-radius': '3px',
              'font-family': 'monospace',
              'font-size': '13px',
            },
          },
        })
      }
    },

    // ── Autocomplete {{ ───────────────────────────────────────────────

    handleRteKeyup: function (e) {
      if (this.autocomplete.visible && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].indexOf(e.key) !== -1) {
        return
      }

      var doc = this.autocomplete.rteEl
      var sel = doc && doc.getSelection && doc.getSelection()
      if (!sel || !sel.rangeCount) return

      var range = sel.getRangeAt(0)
      var textNode = range.startContainer
      if (textNode.nodeType !== 3) { this.hideAutocomplete(); return }

      var text = (textNode.textContent || '').substring(0, range.startOffset)
      var match = text.match(/\{\{\s*([a-zA-Z0-9_]*)$/)

      if (match) {
        var newFilter = match[1].toLowerCase()
        if (this.autocomplete.filter !== newFilter) {
          this.$set(this.autocomplete, 'selectedIndex', 0)
        }
        this.$set(this.autocomplete, 'filter', newFilter)
        this.$set(this.autocomplete, 'items', this.variables)
        this.$set(this.autocomplete, 'visible', true)
        this.$set(this.autocomplete, 'savedRange', range.cloneRange())
        this.positionAutocomplete(range)
      } else {
        this.hideAutocomplete()
      }
    },

    handleRteKeydown: function (e) {
      if (!this.autocomplete.visible) return

      var items = this.filteredAutocompleteItems
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.$set(this.autocomplete, 'selectedIndex', Math.min(this.autocomplete.selectedIndex + 1, items.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.$set(this.autocomplete, 'selectedIndex', Math.max(this.autocomplete.selectedIndex - 1, 0))
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (items.length) {
          e.preventDefault()
          this.selectAutocomplete(items[this.autocomplete.selectedIndex])
        }
      } else if (e.key === 'Escape') {
        this.hideAutocomplete()
      }
    },

    positionAutocomplete: function (range) {
      try {
        var iframeEl = this._grapesjsEditor.Canvas.getFrameEl()
        var iframeRect = iframeEl.getBoundingClientRect()
        var rangeRect = range.getBoundingClientRect()
        this.$set(this.autocomplete, 'x', iframeRect.left + rangeRect.left)
        this.$set(this.autocomplete, 'y', iframeRect.top + rangeRect.bottom + 4)
      } catch (e) {
        this.$set(this.autocomplete, 'x', 200)
        this.$set(this.autocomplete, 'y', 300)
      }
    },

    selectAutocomplete: function (item) {
      var doc = this.autocomplete.rteEl
      var savedRange = this.autocomplete.savedRange
      if (!doc || !savedRange) return

      var textNode = savedRange.startContainer
      if (textNode.nodeType !== 3) return

      var text = textNode.textContent || ''
      var offset = savedRange.startOffset
      var before = text.substring(0, offset)
      var triggerIdx = before.lastIndexOf('{{')
      if (triggerIdx === -1) return

      var replacement = '{{ ' + item.key + ' }}'
      var newText = text.substring(0, triggerIdx) + replacement + text.substring(offset)
      textNode.textContent = newText

      // Restore focus to iframe and set cursor after inserted variable
      var iframeEl = this._grapesjsEditor && this._grapesjsEditor.Canvas.getFrameEl()
      if (iframeEl && iframeEl.contentWindow) iframeEl.contentWindow.focus()

      var sel = doc.getSelection()
      if (sel) {
        var newOffset = triggerIdx + replacement.length
        var newRange = doc.createRange()
        newRange.setStart(textNode, newOffset)
        newRange.setEnd(textNode, newOffset)
        sel.removeAllRanges()
        sel.addRange(newRange)
      }

      this.hideAutocomplete()
    },

    onAutocompleteClick: function (item) {
      var doc = this.autocomplete.rteEl
      var savedRange = this.autocomplete.savedRange
      if (!doc || !savedRange) return

      var textNode = savedRange.startContainer
      if (textNode.nodeType !== 3) return

      var text = textNode.textContent || ''
      var offset = savedRange.startOffset
      var before = text.substring(0, offset)
      var triggerIdx = before.lastIndexOf('{{')
      if (triggerIdx === -1) return

      var replacement = '{{ ' + item.key + ' }}'
      var newText = text.substring(0, triggerIdx) + replacement + text.substring(offset)
      textNode.textContent = newText

      // Hide dropdown first
      this.$set(this.autocomplete, 'visible', false)
      this.$set(this.autocomplete, 'filter', '')
      this.$set(this.autocomplete, 'selectedIndex', 0)
      this.$set(this.autocomplete, 'savedRange', null)

      // Then restore focus + cursor in iframe
      var self = this
      this.$nextTick(function () {
        var iframeEl = self._grapesjsEditor && self._grapesjsEditor.Canvas.getFrameEl()
        if (iframeEl && iframeEl.contentWindow) iframeEl.contentWindow.focus()

        var sel = doc.getSelection()
        if (sel) {
          var newOffset = triggerIdx + replacement.length
          var newRange = doc.createRange()
          newRange.setStart(textNode, newOffset)
          newRange.setEnd(textNode, newOffset)
          sel.removeAllRanges()
          sel.addRange(newRange)
        }
      })
    },

    hideAutocomplete: function () {
      this.$set(this.autocomplete, 'visible', false)
      this.$set(this.autocomplete, 'filter', '')
      this.$set(this.autocomplete, 'selectedIndex', 0)
      this.$set(this.autocomplete, 'savedRange', null)
    },

    loadEditorContent: function () {
      if (!this._grapesjsEditor) return
      var locale = this.activeLocale
      var json = this.localBodyJson[locale]

      if (json && typeof json === 'object' && Object.keys(json).length > 0) {
        this._grapesjsEditor.loadProjectData(json)
      } else {
        var rawHtml = this.localBodyHtml[locale] || ''

        // Extract <style> tags from body_html and load them separately
        var styleMatch = rawHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/gi)
        var css = ''
        var html = rawHtml

        if (styleMatch) {
          css = styleMatch
            .map(function (tag) { return tag.replace(/<\/?style[^>]*>/gi, '') })
            .join('\n')
          html = rawHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        }

        this._grapesjsEditor.setComponents(html)
        if (css) {
          this._grapesjsEditor.setStyle(css)
        }

        // Immediately capture as projectData so next switch preserves styles
        var self = this
        this.$nextTick(function () {
          if (self._grapesjsEditor) {
            self.$set(self.localBodyJson, locale, self._grapesjsEditor.getProjectData())
          }
        })
      }
    },

    saveEditorContent: function () {
      if (!this._grapesjsEditor) return
      var locale = this.activeLocale
      var html = this._grapesjsEditor.getHtml() || ''
      var css = this._grapesjsEditor.getCss() || ''
      this.$set(this.localBodyHtml, locale, css ? '<style>' + css + '</style>' + html : html)
      this.$set(this.localBodyJson, locale, this._grapesjsEditor.getProjectData())
    },

    onLocaleChange: function (loc) {
      if (this._grapesjsEditor) {
        this.saveEditorContent()
      }
      this.activeLocale = loc
      var self = this
      this.$nextTick(function () { self.loadEditorContent() })
    },

    copyLocale: function (fromLocale) {
      if (this._grapesjsEditor) this.saveEditorContent()

      this.$set(this.localSubject, this.activeLocale, this.localSubject[fromLocale] || '')
      this.$set(this.localBodyHtml, this.activeLocale, this.localBodyHtml[fromLocale] || '')
      this.$set(this.localBodyJson, this.activeLocale, this.localBodyJson[fromLocale]
        ? JSON.parse(JSON.stringify(this.localBodyJson[fromLocale]))
        : {})

      var self = this
      this.$nextTick(function () { self.loadEditorContent() })
    },

    insertVariable: function (key) {
      var tag = '{{ ' + key + ' }}'

      // If subject input was last focused, insert into subject
      if (this.lastFocusedInput === 'subject' && this.$refs.subjectInputRef) {
        var input = this.$refs.subjectInputRef
        var start = input.selectionStart != null ? input.selectionStart : (this.localSubject[this.activeLocale] || '').length
        var end = input.selectionEnd != null ? input.selectionEnd : start
        var current = this.localSubject[this.activeLocale] || ''
        this.$set(this.localSubject, this.activeLocale, current.substring(0, start) + tag + current.substring(end))
        var self = this
        this.$nextTick(function () {
          input.focus()
          var newPos = start + tag.length
          input.setSelectionRange(newPos, newPos)
        })
        return
      }

      if (!this._grapesjsEditor) return

      // If RTE is active (inline editing), insert at cursor position
      if (this.autocomplete.rteEl) {
        var sel = this.autocomplete.rteEl.getSelection()
        if (sel && sel.rangeCount) {
          var range = sel.getRangeAt(0)
          range.deleteContents()
          range.insertNode(this.autocomplete.rteEl.createTextNode(tag))
          range.collapse(false)
          sel.removeAllRanges()
          sel.addRange(range)
          return
        }
      }

      // Otherwise, append to selected text component or add new
      var selected = this._grapesjsEditor.getSelected()
      if (selected && selected.is('text')) {
        selected.set('content', (selected.get('content') || '') + tag)
      } else {
        this._grapesjsEditor.addComponents({
          type: 'text',
          content: tag,
          style: {
            display: 'inline-block',
            padding: '2px 6px',
            background: '#e3f2fd',
            color: '#1565c0',
            'border-radius': '3px',
            'font-family': 'monospace',
          },
        })
      }
    },

    // ── Save ──────────────────────────────────────────────────────────

    async save() {
      if (this._grapesjsEditor) this.saveEditorContent()

      this.saving = true
      try {
        await this.store.updateTemplate(this.holderType, this.holderId, this.templateData.id, {
          subject: this.localSubject,
          body_html: this.localBodyHtml,
          body_json: this.localBodyJson,
          metadata: this.localMetadata,
        })
        this.$ecNotify(this.$ecTranslate('MV2.TEMPLATE_SAVED'), 'success')
        this.$emit('saved')
      } catch (err) {
        var msg = (err && err.response && err.response.data && err.response.data.message) || this.$ecTranslate('MV2.SAVE_ERROR')
        this.$ecNotify(msg, 'error')
      } finally {
        this.saving = false
      }
    },

    // ── Send Test ─────────────────────────────────────────────────────

    async doSendTest() {
      if (!this.sendTestEmail) return
      try {
        await this.store.sendTest(this.holderType, this.holderId, {
          template_id: this.templateData.id,
          locale: this.sendTestLocale,
          email: this.sendTestEmail,
        })
        this.$ecNotify(this.$ecTranslate('MV2.TEST_SENT'), 'success')
        this.showTestPanel = false
      } catch (e) {
        this.$ecNotify(this.$ecTranslate('MV2.TEST_ERROR'), 'error')
      }
    },

    // ── Lifecycle helpers ─────────────────────────────────────────────

    onClickOutsideCopy: function (e) {
      if (this.showCopyMenu && !(e.target && e.target.closest && e.target.closest('.ec-mv2-copy-dropdown-wrapper'))) {
        this.showCopyMenu = false
      }
    },
  },
}
</script>

<template>
  <div class="ec-mv2-editor">
    <!-- ── HEADER : Locale + Subject + Actions (1 ligne) ───────────── -->
    <div class="ec-mv2-header">
      <div class="ec-mv2-header-left">
        <div class="ec-mv2-locale-tabs">
          <button
            v-for="loc in locales"
            :key="loc"
            type="button"
            class="ec-mv2-locale-tab"
            :class="{ 'ec-mv2-locale-tab--active': activeLocale === loc }"
            @click="onLocaleChange(loc)"
          >
            {{ loc.toUpperCase() }}
          </button>
        </div>
        <div v-if="locales.length > 1" class="ec-mv2-copy-dropdown-wrapper">
          <button type="button" class="ec-mv2-icon-btn" @click="showCopyMenu = !showCopyMenu" :title="$ecTranslate('MV2.COPY_FROM')">
            <i class="bi bi-clipboard" />
          </button>
          <div v-if="showCopyMenu" class="ec-mv2-copy-menu">
            <button
              v-for="loc in copyableLocales"
              :key="loc"
              type="button"
              class="ec-mv2-copy-menu-item"
              @click="copyLocale(loc); showCopyMenu = false"
            >
              {{ loc.toUpperCase() }} &rarr; {{ activeLocale.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
      <div class="ec-mv2-header-subject">
        <span class="ec-mv2-subject-label">{{ $ecTranslate('MV2.SUBJECT') }}</span>
        <input
          ref="subjectInputRef"
          :value="localSubject[activeLocale]"
          @input="$set(localSubject, activeLocale, $event.target.value)"
          type="text"
          class="ec-mv2-input"
          :placeholder="$ecTranslate('MV2.SUBJECT_PLACEHOLDER')"
          @focus="lastFocusedInput = 'subject'"
        />
      </div>
      <div class="ec-mv2-header-actions">
        <button type="button" class="ec-mv2-icon-btn" :class="{ 'ec-mv2-icon-btn--active': showSender }" @click="showSender = !showSender" :title="$ecTranslate('MV2.SENDER_OVERRIDE')">
          <i class="bi bi-envelope-open" />
        </button>
        <button type="button" class="ec-mv2-icon-btn" :class="{ 'ec-mv2-icon-btn--active': showTestPanel }" @click="showTestPanel = !showTestPanel" :title="$ecTranslate('MV2.SEND_TEST')">
          <i class="bi bi-send" />
        </button>
        <button type="button" class="ec-mv2-icon-btn" :class="{ 'ec-mv2-icon-btn--active': showTypeSettings }" @click="showTypeSettings = !showTypeSettings" :title="$ecTranslate('MV2.TYPE_SETTINGS')">
          <i class="bi bi-gear" />
        </button>
        <div class="ec-mv2-header-sep"></div>
        <EcButton type="button" variant="outline-secondary" @click="$emit('cancel')">
          {{ $ecTranslate('MV2.CANCEL') }}
        </EcButton>
        <EcButton type="button" variant="primary" :loading="saving" :disabled="saving" icon="save" @click="save">
          {{ $ecTranslate('MV2.SAVE') }}
        </EcButton>
      </div>
    </div>

    <!-- ── EXPANDABLE PANELS (cards) ──────────────────────────────── -->
    <transition name="ec-mv2-slide">
      <div v-if="showSender || showTestPanel || showTypeSettings" class="ec-mv2-panels">
        <div v-if="showSender" class="ec-mv2-card">
          <div class="ec-mv2-card-header">
            <span><i class="bi bi-envelope-open" /> {{ $ecTranslate('MV2.SENDER_OVERRIDE') }}</span>
            <button type="button" class="ec-mv2-card-close" @click="showSender = false"><i class="bi bi-x" /></button>
          </div>
          <div class="ec-mv2-card-body ec-mv2-card-grid-3">
            <div class="ec-mv2-field">
              <label>{{ $ecTranslate('MV2.FROM_NAME') }}</label>
              <input type="text" class="ec-mv2-input" v-model="localMetadata.from_name" />
            </div>
            <div class="ec-mv2-field">
              <label>{{ $ecTranslate('MV2.FROM_EMAIL') }}</label>
              <input type="email" class="ec-mv2-input" v-model="localMetadata.from_email" />
            </div>
            <div class="ec-mv2-field">
              <label>{{ $ecTranslate('MV2.REPLY_TO') }}</label>
              <input type="email" class="ec-mv2-input" v-model="localMetadata.reply_to_email" />
            </div>
          </div>
        </div>

        <div v-if="showTestPanel" class="ec-mv2-card">
          <div class="ec-mv2-card-header">
            <span><i class="bi bi-send" /> {{ $ecTranslate('MV2.SEND_TEST') }}</span>
            <button type="button" class="ec-mv2-card-close" @click="showTestPanel = false"><i class="bi bi-x" /></button>
          </div>
          <div class="ec-mv2-card-body ec-mv2-card-grid-3">
            <div class="ec-mv2-field">
              <label>{{ $ecTranslate('MV2.DEFAULT_LOCALE') }}</label>
              <select v-model="sendTestLocale" class="ec-mv2-input">
                <option v-for="loc in locales" :key="loc" :value="loc">{{ loc.toUpperCase() }}</option>
              </select>
            </div>
            <div class="ec-mv2-field">
              <label>Email</label>
              <input type="email" class="ec-mv2-input" v-model="sendTestEmail" :placeholder="$ecTranslate('MV2.TEST_EMAIL_PLACEHOLDER')" @keyup.enter="doSendTest" />
            </div>
            <div class="ec-mv2-field" style="display:flex;align-items:flex-end;">
              <EcButton variant="primary" icon="send" @click="doSendTest" :disabled="!sendTestEmail">
                {{ $ecTranslate('MV2.SEND') }}
              </EcButton>
            </div>
          </div>
        </div>

        <div v-if="showTypeSettings" class="ec-mv2-card">
          <div class="ec-mv2-card-header">
            <span><i class="bi bi-gear" /> {{ $ecTranslate('MV2.TYPE_SETTINGS') }}</span>
            <button type="button" class="ec-mv2-card-close" @click="showTypeSettings = false"><i class="bi bi-x" /></button>
          </div>
          <div class="ec-mv2-card-body">
            <div class="ec-mv2-type-info">
              <strong>{{ getTranslatable(mailType.type_name, activeLocale) }}</strong>
              <span class="ec-mv2-type-badge">{{ mailType.code }}</span>
              <span class="ec-mv2-type-badge ec-mv2-type-badge--category">{{ mailType.category }}</span>
            </div>
            <p v-if="mailType.description" class="ec-mv2-type-desc">{{ getTranslatable(mailType.description, activeLocale) }}</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── VARIABLES BAR (compact, scrollable) ─────────────────────── -->
    <div v-if="variables.length" class="ec-mv2-variables-bar">
      <i class="bi bi-code-slash ec-mv2-variables-icon" />
      <div class="ec-mv2-variables-chips">
        <button
          v-for="v in variables"
          :key="v.key"
          type="button"
          class="ec-mv2-var-chip"
          @mousedown.prevent="insertVariable(v.key)"
          :title="getTranslatable(v.label, activeLocale)"
        >
          {{ v.key }}
        </button>
      </div>
    </div>

    <!-- ── GRAPESJS EDITOR (full remaining height) ──────────────────── -->
    <div class="ec-mv2-editor-body">
      <div class="ec-mv2-gjs-wrapper">
        <div ref="editorContainer" class="ec-mv2-gjs-container" />
      </div>
    </div>

    <!-- ── AUTOCOMPLETE DROPDOWN (inline, no teleport in Vue 2) ───── -->
    <div
      v-if="autocomplete.visible && filteredAutocompleteItems.length"
      class="ec-mv2-autocomplete"
      :style="{ left: autocomplete.x + 'px', top: autocomplete.y + 'px' }"
    >
      <div class="ec-mv2-autocomplete-header">VARIABLES</div>
      <div
        v-for="(item, idx) in filteredAutocompleteItems"
        :key="item.key"
        class="ec-mv2-autocomplete-item"
        :class="{ 'ec-mv2-autocomplete-item--active': idx === autocomplete.selectedIndex }"
        @mousedown.prevent.stop
        @click.prevent.stop="onAutocompleteClick(item)"
        @mouseenter="autocomplete.selectedIndex = idx"
      >
        <code>{{ varTag(item.key) }}</code>
        <span class="ec-mv2-autocomplete-label">{{ getTranslatable(item.label, activeLocale) }}</span>
      </div>
    </div>
  </div>
</template>

<style>
/* ── GrapesJS global overrides — must NOT be scoped ───────────────── */

/* Theme colors */
.ec-mv2-gjs-wrapper .gjs-one-bg { background-color: #f9fafb !important; }
.ec-mv2-gjs-wrapper .gjs-two-color { color: #374151 !important; }
.ec-mv2-gjs-wrapper .gjs-three-bg { background-color: #0091ca !important; }
.ec-mv2-gjs-wrapper .gjs-four-color,
.ec-mv2-gjs-wrapper .gjs-four-color-h:hover { color: #0091ca !important; }

/* Force editor + canvas to fill their container */
.ec-mv2-gjs-wrapper .gjs-editor {
  width: 100% !important;
  height: 100% !important;
}
.ec-mv2-gjs-wrapper .gjs-editor-cont {
  width: 100% !important;
  height: 100% !important;
}
.ec-mv2-gjs-wrapper .gjs-cv-canvas {
  width: calc(100% - 220px) !important;
  height: 100% !important;
  top: 0;
  left: 0 !important;
  background-color: #e5e7eb !important;
}
.ec-mv2-gjs-wrapper .gjs-frame-wrapper {
  height: 100% !important;
}

/* Container generated by GrapesJS.init() must also fill */
.ec-mv2-gjs-container > div {
  height: 100% !important;
  width: 100% !important;
  border: none !important;
}

/* Panel tweaks */
.ec-mv2-gjs-wrapper .gjs-pn-panel { padding: 0 !important; }

/* Right panels — views container */
.ec-mv2-gjs-wrapper .gjs-pn-views-container {
  width: 220px !important;
  min-width: 220px !important;
  overflow-y: auto !important;
}

/* Block manager — 2 column grid like manager */
.ec-mv2-gjs-wrapper .gjs-blocks-c {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 6px !important;
  padding: 8px !important;
}
.ec-mv2-gjs-wrapper .gjs-block {
  width: auto !important;
  min-height: 56px !important;
  padding: 8px 4px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  border-radius: 6px !important;
  border: 1px solid #e5e7eb !important;
  background: #fff !important;
  cursor: grab !important;
  transition: border-color 0.15s, box-shadow 0.15s !important;
  margin: 0 !important;
}
.ec-mv2-gjs-wrapper .gjs-block:hover {
  border-color: #0091ca !important;
  box-shadow: 0 2px 6px rgba(0, 145, 202, 0.12) !important;
}
.ec-mv2-gjs-wrapper .gjs-block__media {
  margin-bottom: 2px !important;
  font-size: 18px !important;
}
.ec-mv2-gjs-wrapper .gjs-block-label {
  font-size: 10px !important;
  line-height: 1.2 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 90px !important;
}

/* Block category headers */
.ec-mv2-gjs-wrapper .gjs-block-category .gjs-title {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 6px 10px !important;
  border-bottom: 1px solid #e5e7eb !important;
  background: #f9fafb !important;
  color: #6b7280 !important;
}
</style>

<style scoped>
.ec-mv2-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  -ms-flex: 1 1 0%;
  flex: 1 1 0%;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Header (1 ligne compacte) ──────────────────────────────────────── */
.ec-mv2-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid var(--ec-border-color, #e9ecef);
}
.ec-mv2-header-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.ec-mv2-header-subject {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ec-mv2-subject-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  flex-shrink: 0;
}
.ec-mv2-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.ec-mv2-header-sep {
  width: 1px;
  height: 24px;
  background: var(--ec-border-color, #e0e0e0);
  margin: 0 4px;
}

/* ── Locale tabs ────────────────────────────────────────────────────── */
.ec-mv2-locale-tabs {
  display: flex;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 2px;
}
.ec-mv2-locale-tab {
  padding: 4px 12px;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.5px;
}
.ec-mv2-locale-tab:hover { color: #374151; }
.ec-mv2-locale-tab--active {
  background: var(--ec-primary-color, #0091ca);
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 145, 202, 0.3);
}

/* ── Icon buttons ───────────────────────────────────────────────────── */
.ec-mv2-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
}
.ec-mv2-icon-btn:hover { color: #374151; background: #f3f4f6; }
.ec-mv2-icon-btn--active { color: var(--ec-primary-color, #0091ca); background: #e0f3ff; }

/* ── Copy dropdown ──────────────────────────────────────────────────── */
.ec-mv2-copy-dropdown-wrapper { position: relative; }
.ec-mv2-copy-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 140px;
  background: #fff;
  border: 1px solid var(--ec-border-color, #dee2e6);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
}
.ec-mv2-copy-menu-item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: background 0.1s;
}
.ec-mv2-copy-menu-item:hover { background: #f3f4f6; }

/* ── Card panels ────────────────────────────────────────────────────── */
.ec-mv2-panels {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.ec-mv2-panels::-webkit-scrollbar { display: none; }
.ec-mv2-card {
  background: #fff;
  border: 1px solid var(--ec-border-color, #e5e7eb);
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 260px;
  overflow: hidden;
}
.ec-mv2-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid var(--ec-border-color, #e9ecef);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6b7280;
}
.ec-mv2-card-header span {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ec-mv2-card-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
}
.ec-mv2-card-close:hover { background: #e5e7eb; color: #374151; }
.ec-mv2-card-body {
  padding: 12px 14px;
}
.ec-mv2-card-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.ec-mv2-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 3px;
}

/* ── Slide transition ────────────────────────────────────────────────── */
.ec-mv2-slide-enter-active,
.ec-mv2-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}
.ec-mv2-slide-enter,
.ec-mv2-slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Type info ───────────────────────────────────────────────────────── */
.ec-mv2-type-info {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  flex-wrap: wrap;
}
.ec-mv2-type-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
}
.ec-mv2-type-badge--category {
  background: #fef3c7;
  color: #92400e;
}
.ec-mv2-type-desc {
  color: #6b7280;
  font-size: 12px;
}

/* ── Variables bar ──────────────────────────────────────────────────── */
.ec-mv2-variables-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--ec-border-color, #e9ecef);
  overflow-x: auto;
  scrollbar-width: none;
}
.ec-mv2-variables-bar::-webkit-scrollbar { display: none; }
.ec-mv2-variables-icon {
  color: #9ca3af;
  flex-shrink: 0;
  font-size: 13px;
}
.ec-mv2-variables-chips {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
}
.ec-mv2-var-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
  border-radius: 4px;
  font-family: 'Fira Code', monospace;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.ec-mv2-var-chip:hover {
  background: #bbdefb;
  border-color: #90caf9;
  transform: translateY(-1px);
}

/* ── GrapesJS editor area ────────────────────────────────────────────── */
.ec-mv2-editor-body {
  display: flex;
  -ms-flex: 1 1 0%;
  flex: 1 1 0%;
  min-height: 0;
  height: 0;
  border: 1px solid var(--ec-border-color, #e9ecef);
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  margin-top: -1px;
}
.ec-mv2-gjs-wrapper {
  -ms-flex: 1 1 0%;
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  position: relative;
  overflow: hidden;
}
.ec-mv2-gjs-container {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* ── Common form elements ────────────────────────────────────────────── */
.ec-mv2-input {
  padding: 7px 12px;
  border: 1px solid var(--ec-border-color, #dee2e6);
  border-radius: 6px;
  font-size: 13px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.ec-mv2-input:focus {
  outline: none;
  border-color: var(--ec-primary-color, #0091ca);
  box-shadow: 0 0 0 2px rgba(0, 145, 202, 0.1);
}
.ec-mv2-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid var(--ec-border-color, #dee2e6);
  background: #fff;
  color: #374151;
  transition: all 0.15s;
  white-space: nowrap;
}
.ec-mv2-btn:hover { background: #f9fafb; border-color: #cbd5e1; }
.ec-mv2-btn--primary {
  background: var(--ec-primary-color, #0091ca);
  color: #fff;
  border-color: var(--ec-primary-color, #0091ca);
}
.ec-mv2-btn--primary:hover:not(:disabled) { opacity: 0.9; }
.ec-mv2-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.ec-mv2-spin { animation: ec-spin 1s linear infinite; }
@keyframes ec-spin { to { transform: rotate(360deg); } }
</style>

<style>
/* Autocomplete dropdown — must NOT be scoped (positioned fixed on body) */
.ec-mv2-autocomplete {
  position: fixed;
  z-index: 99999;
  min-width: 260px;
  max-width: 380px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-family: inherit;
}
.ec-mv2-autocomplete-header {
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #9ca3af;
  border-bottom: 1px solid #f0f0f0;
  letter-spacing: 0.5px;
}
.ec-mv2-autocomplete-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}
.ec-mv2-autocomplete-item:hover,
.ec-mv2-autocomplete-item--active {
  background: #f0f7ff;
}
.ec-mv2-autocomplete-item code {
  font-size: 12px;
  color: #0091ca;
  font-weight: 600;
  white-space: nowrap;
}
.ec-mv2-autocomplete-label {
  font-size: 12px;
  color: #6b7280;
}
</style>
